import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Collapse,
  CopyButton,
  Divider,
  Group,
  List,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { measure } from "@how/communications";
import { semanticColor, type SemanticRole } from "@how/design-system";
import { ConfirmActionModal } from "@how/ui";
import { env } from "../../lib/env";
import { formatPhone } from "../communications/format-phone";
import { useSmsSender, type SmsSender } from "../communications/sms-sender-queries";
import {
  confirmationAsSent,
  incomingTextsAddress,
  isAccountSid,
  isE164,
  isMessagingServiceSid,
  keywordProblems,
  MAX_REPLY_LENGTH,
  type KeywordDraft,
} from "./messaging-rules";
import {
  useConnectTwilio,
  useDisconnectTwilio,
  useMessagingConnection,
  useSetKeyword,
  useSetMessagingService,
  type ConnectionStatus,
  type MessagingConnection,
} from "./messaging-queries";

const LABEL: Record<ConnectionStatus, string> = {
  pending: "Set up, not yet verified",
  connected: "Connected",
  error: "Needs attention",
  revoked: "Turned off",
};
const ROLE: Record<ConnectionStatus, SemanticRole> = {
  pending: "warning",
  connected: "success",
  error: "danger",
  revoked: "neutral",
};

const SUPPORT_EMAIL = "support@dcxlogic.com";

/**
 * Text messaging (ADR 0010, ADR 0035; #319, #320). HoW sends a church's texts from a number DCX Logic sets up with
 * Twilio and looks after (owner, 2026-10-02): the church sees its number, its status, the Messaging Service and its
 * text-to-join keyword, and never needs Twilio's credentials. The form that takes them stays, folded away, for
 * connecting with details DCX Logic hands over, until DCX provisions numbers from the control plane (#246); the auth
 * token goes straight to Vault through connect_integration and can never be read back. Needs integrations.manage and
 * the Integrations module.
 */
export function MessagingCard() {
  const canManage = usePermission("integrations.manage", { module: "integrations", write: true });
  const connection = useMessagingConnection(canManage.data === true);
  const sender = useSmsSender(canManage.data === true);

  if (canManage.data !== true) return null;
  const current = connection.data ?? null;
  const live = current !== null && current.status !== "revoked";
  const pilot = sender.data?.pilot === true ? sender.data : null;

  if (pilot !== null) return <PilotSender sender={pilot} ownNumber={live ? current.from : null} />;

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="xs">
          <div>
            <Title order={4}>Text messaging</Title>
            <Text size="sm" c="dimmed" maw={600}>
              HoW sends your church&apos;s texts from your church&apos;s own number. DCX Logic sets
              the number up and looks after it with the carriers, so there is nothing to manage in
              Twilio.
            </Text>
          </div>
          {current === null ? null : (
            <Badge
              variant="light"
              color={semanticColor(ROLE[current.status])}
              style={{ flexShrink: 0 }}
            >
              {LABEL[current.status]}
            </Badge>
          )}
        </Group>
        {live ? <Connected connection={current} /> : <NotSetUp />}
      </Stack>
    </Card>
  );
}

/**
 * Pilot texting (ADR 0072): DCX Logic has the church's texts going out from its registered pilot sender, to opted-in
 * pilot testers only. The card shows that sender and its number instead of the church's own; there is nothing to set
 * up or change here, and the church's own number (if it has one) is not used meanwhile.
 */
function PilotSender({
  sender,
  ownNumber,
}: {
  readonly sender: SmsSender;
  readonly ownNumber: string | null;
}) {
  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="xs">
          <div>
            <Title order={4}>Text messaging</Title>
            <Text size="sm" c="dimmed" maw={600}>
              Pilot texting is on. DCX Logic sends your church&apos;s texts from its registered
              pilot sender, to opted-in pilot testers only, for demos and pilots.
            </Text>
          </div>
          <Badge variant="light" color={semanticColor("info")} style={{ flexShrink: 0 }}>
            Pilot texting
          </Badge>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <div>
            <Text size="xs" c="dimmed">
              Sender
            </Text>
            <Text size="sm">{sender.senderName}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Number
            </Text>
            <Text size="sm" className="how-numeric">
              {sender.from === null ? "Not set" : formatPhone(sender.from)}
            </Text>
          </div>
        </SimpleGrid>
        <Text size="sm">
          {`Every text ends "${sender.senderName}: Reply STOP to opt out". Someone who replies STOP stops getting pilot texts from every church using this number.`}
        </Text>
        {ownNumber === null ? null : (
          <Text size="sm" c="dimmed">
            {`Your church's own number, ${formatPhone(ownNumber)}, isn't used while pilot texting is on.`}
          </Text>
        )}
        <Text size="sm" c="dimmed">
          To turn pilot texting off, or to text your whole church from your own number, email{" "}
          <Anchor href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Anchor>.
        </Text>
      </Stack>
    </Card>
  );
}

/** No number yet: DCX Logic sets it up; the details form is folded away for when they hand details over. */
function NotSetUp() {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap="sm">
      <Text size="sm">
        Texting isn&apos;t set up for your church yet. To turn it on, email{" "}
        <Anchor href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Anchor>.
      </Text>
      <div>
        <Button
          variant="subtle"
          size="compact-sm"
          aria-expanded={open}
          aria-controls="messaging-connect-form"
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          {open ? "Hide the set-up details" : "Set up with details from DCX Logic"}
        </Button>
      </div>
      <Collapse in={open} id="messaging-connect-form">
        <ConnectForm />
      </Collapse>
    </Stack>
  );
}

function ConnectForm() {
  const connect = useConnectTwilio();
  const [accountSid, setAccountSid] = useState("");
  const [token, setToken] = useState("");
  const [from, setFrom] = useState("");
  const [serviceSid, setServiceSid] = useState("");
  const sidError =
    accountSid.trim() !== "" && !isAccountSid(accountSid)
      ? "An account SID is AC followed by 32 letters and digits."
      : null;
  const fromError =
    from.trim() !== "" && !isE164(from) ? "Use international form, like +15551234567." : null;
  const serviceError =
    serviceSid.trim() !== "" && !isMessagingServiceSid(serviceSid)
      ? "A Messaging Service SID is MG followed by 32 letters and digits."
      : null;
  const ready =
    isAccountSid(accountSid) &&
    token !== "" &&
    isE164(from) &&
    (serviceSid.trim() === "" || isMessagingServiceSid(serviceSid));
  return (
    <Stack gap="sm" pt="xs">
      <Text size="sm" c="dimmed" maw={600}>
        Enter the details DCX Logic gave you. The auth token goes into the platform&apos;s vault the
        moment you save it and can&apos;t be read back, here or anywhere.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label="Account SID"
          autoComplete="off"
          value={accountSid}
          error={sidError}
          onChange={(event) => {
            setAccountSid(event.currentTarget.value);
          }}
        />
        <PasswordInput
          label="Auth token"
          autoComplete="off"
          value={token}
          onChange={(event) => {
            setToken(event.currentTarget.value);
          }}
        />
        <TextInput
          label="Sending number"
          placeholder="+15551234567"
          value={from}
          error={fromError}
          onChange={(event) => {
            setFrom(event.currentTarget.value);
          }}
        />
        <TextInput
          label="Messaging Service SID (optional)"
          placeholder="MG…"
          autoComplete="off"
          value={serviceSid}
          error={serviceError}
          description="If there is one, texts go through it and its opt-out settings apply."
          onChange={(event) => {
            setServiceSid(event.currentTarget.value);
          }}
        />
      </SimpleGrid>
      <Group>
        <Button
          loading={connect.isPending}
          disabled={!ready}
          onClick={() => {
            connect.mutate(
              { accountSid, token, from, messagingServiceSid: serviceSid },
              {
                onSuccess: () => {
                  setToken("");
                },
              },
            );
          }}
        >
          Set up texting
        </Button>
      </Group>
      {connect.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {connect.error.message}
        </Alert>
      ) : null}
    </Stack>
  );
}

function Connected({ connection }: { connection: MessagingConnection }) {
  const disconnect = useDisconnectTwilio();
  const [confirming, setConfirming] = useState(false);
  const address = incomingTextsAddress(env.VITE_HOW_PLATFORM_BASE_DOMAIN, connection.id);
  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Text size="sm">
          Texts go out from <strong>{connection.from ?? "no number yet"}</strong>.
        </Text>
        {connection.status === "pending" ? (
          <Text size="sm" c="dimmed">
            It shows as verified once the carriers accept the first text HoW sends from it.
          </Text>
        ) : null}
        {connection.lastError === null ? null : (
          <Alert color={semanticColor("danger")} variant="light" title="Last problem">
            {connection.lastError}
          </Alert>
        )}
      </Stack>
      <MessagingServiceField connection={connection} />
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          Incoming texts address
        </Text>
        <Text size="sm" c="dimmed" maw={600}>
          What DCX Logic sets on the number in Twilio, so replies, STOP and your keyword reach HoW.
        </Text>
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Code style={{ overflowWrap: "anywhere", minWidth: 0 }} data-testid="incoming-address">
            {address}
          </Code>
          <CopyButton value={address}>
            {({ copied, copy }) => (
              <Button
                variant="default"
                size="compact-sm"
                onClick={copy}
                style={{ flexShrink: 0 }}
                aria-label={
                  copied ? "Copied the incoming texts address" : "Copy the incoming texts address"
                }
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            )}
          </CopyButton>
        </Group>
      </Stack>
      <Divider />
      <KeywordSection connection={connection} />
      <Divider />
      <Group>
        <Button
          variant="subtle"
          color={semanticColor("danger")}
          onClick={() => {
            setConfirming(true);
          }}
        >
          Turn off texting
        </Button>
      </Group>
      {disconnect.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {disconnect.error.message}
        </Alert>
      ) : null}
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          disconnect.mutate(connection.id, {
            onSettled: () => {
              setConfirming(false);
            },
          });
        }}
        title="Turn off texting?"
        message={`Texts stop going out from ${connection.from ?? "your number"}, and replies to it stop reaching HoW. DCX Logic can turn it back on.`}
        confirmLabel="Turn off texting"
        danger
        loading={disconnect.isPending}
      />
    </Stack>
  );
}

/** The Messaging Service on a live connection (#320): shown, changed or removed without reconnecting. */
function MessagingServiceField({ connection }: { connection: MessagingConnection }) {
  const save = useSetMessagingService();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(connection.messagingServiceSid ?? "");
  const invalid = value.trim() !== "" && !isMessagingServiceSid(value);
  if (!editing) {
    return (
      <Group gap="xs" wrap="wrap">
        <Text size="sm">
          Messaging Service:{" "}
          {connection.messagingServiceSid === null ? (
            <Text span c="dimmed" size="sm">
              none, texts go straight from the number
            </Text>
          ) : (
            <Code>{connection.messagingServiceSid}</Code>
          )}
        </Text>
        <Button
          variant="subtle"
          size="compact-sm"
          aria-label={
            connection.messagingServiceSid === null
              ? "Add a Messaging Service"
              : "Change the Messaging Service"
          }
          onClick={() => {
            setValue(connection.messagingServiceSid ?? "");
            setEditing(true);
          }}
        >
          {connection.messagingServiceSid === null ? "Add" : "Change"}
        </Button>
      </Group>
    );
  }
  return (
    <Stack gap="xs">
      <TextInput
        label="Messaging Service SID"
        placeholder="MG…"
        autoComplete="off"
        maw={420}
        value={value}
        error={invalid ? "A Messaging Service SID is MG followed by 32 letters and digits." : null}
        description="Texts go through it, so its opt-out settings apply. Leave it empty to send straight from the number."
        onChange={(event) => {
          setValue(event.currentTarget.value);
        }}
      />
      <Group gap="xs">
        <Button
          size="compact-sm"
          loading={save.isPending}
          disabled={invalid}
          onClick={() => {
            const sid = value.trim();
            save.mutate(
              { connectionId: connection.id, sid: sid === "" ? null : sid },
              {
                onSuccess: () => {
                  setEditing(false);
                },
              },
            );
          }}
        >
          Save Messaging Service
        </Button>
        <Button
          size="compact-sm"
          variant="default"
          onClick={() => {
            setEditing(false);
          }}
        >
          Cancel
        </Button>
      </Group>
      {save.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {save.error.message}
        </Alert>
      ) : null}
    </Stack>
  );
}

/** Characters and segments of one reply, as a carrier counts them. */
function Measure({ value }: { value: string }) {
  const m = measure(value);
  const over = value.length > MAX_REPLY_LENGTH;
  return (
    <Text size="xs" c={over ? semanticColor("danger") : "dimmed"}>
      {value.length} of {MAX_REPLY_LENGTH} characters · {m.segments} segment
      {m.segments === 1 ? "" : "s"}
    </Text>
  );
}

/** The text-to-join keyword (#319, `set_messaging_keyword`). */
function KeywordSection({ connection }: { connection: MessagingConnection }) {
  const save = useSetKeyword();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const saved = connection.keyword;
  const [draft, setDraft] = useState<KeywordDraft>({
    keyword: saved?.keyword ?? "",
    confirmWord: saved?.confirmWord ?? "YES",
    welcome: saved?.welcome ?? "",
    confirmation: saved?.confirmation ?? "",
    joinUrl: saved?.joinUrl ?? "",
  });
  const problems = keywordProblems(draft);
  const changed =
    saved === null ||
    draft.keyword.trim().toUpperCase() !== saved.keyword ||
    draft.confirmWord.trim().toUpperCase() !== saved.confirmWord ||
    draft.welcome !== saved.welcome ||
    draft.confirmation !== saved.confirmation ||
    draft.joinUrl.trim() !== saved.joinUrl;
  const set = (field: keyof KeywordDraft) => (event: { currentTarget: { value: string } }) => {
    const next = event.currentTarget.value;
    setDraft((current) => ({ ...current, [field]: next }));
  };

  return (
    <Stack gap="sm">
      <div>
        <Title order={5}>Text-to-join keyword</Title>
        <Text size="sm" c="dimmed" maw={640}>
          Someone who texts the keyword to your number gets the welcome; replying with the confirm
          word within a day gets the confirmation. It doesn&apos;t add them to your campaigns:
          consent still comes from a form or your staff.
        </Text>
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" maw={480}>
        <TextInput
          label="Keyword"
          placeholder="JOIN"
          value={draft.keyword}
          onChange={set("keyword")}
        />
        <TextInput label="Confirm word" value={draft.confirmWord} onChange={set("confirmWord")} />
      </SimpleGrid>
      <div>
        <Textarea
          label="Welcome"
          description="Asks them to reply with the confirm word, and says how to stop (STOP) and get help (HELP)."
          autosize
          minRows={2}
          value={draft.welcome}
          onChange={set("welcome")}
        />
        <Measure value={draft.welcome} />
      </div>
      <div>
        <Textarea
          label="Confirmation"
          description="Says how to stop (STOP). Write {join} where their personal sign-up link goes."
          autosize
          minRows={2}
          value={draft.confirmation}
          onChange={set("confirmation")}
        />
        <Measure value={confirmationAsSent(draft)} />
      </div>
      <TextInput
        label="Join page"
        placeholder="https://yourchurch.org/join"
        description="Where {join} sends them. Their sign-up there is linked to the number they texted from."
        maw={480}
        value={draft.joinUrl}
        onChange={set("joinUrl")}
      />
      {problems.length > 0 && (draft.keyword !== "" || draft.welcome !== "" || saved !== null) ? (
        <Alert color={semanticColor("warning")} variant="light" title="Before you can save">
          <List size="sm" spacing={2}>
            {problems.map((problem) => (
              <List.Item key={problem}>{problem}</List.Item>
            ))}
          </List>
        </Alert>
      ) : null}
      <Text size="xs" c="dimmed" maw={640}>
        Carriers need every keyword listed in your church&apos;s texting registration. DCX Logic
        adds it when it registers your church, so tell {SUPPORT_EMAIL} before you change it.
      </Text>
      <Group gap="xs">
        <Button
          loading={save.isPending && !confirmRemove}
          disabled={problems.length > 0 || !changed}
          onClick={() => {
            save.mutate({ connectionId: connection.id, draft });
          }}
        >
          {saved === null ? "Save keyword" : "Save changes"}
        </Button>
        {saved === null ? null : (
          <Button
            variant="subtle"
            color={semanticColor("danger")}
            onClick={() => {
              setConfirmRemove(true);
            }}
          >
            Remove keyword {saved.keyword}
          </Button>
        )}
      </Group>
      {save.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {save.error.message}
        </Alert>
      ) : null}
      {saved === null ? null : (
        <ConfirmActionModal
          opened={confirmRemove}
          onClose={() => {
            setConfirmRemove(false);
          }}
          onConfirm={() => {
            save.mutate(
              { connectionId: connection.id, draft: null },
              {
                onSuccess: () => {
                  setDraft({
                    keyword: "",
                    confirmWord: "YES",
                    welcome: "",
                    confirmation: "",
                    joinUrl: "",
                  });
                },
                onSettled: () => {
                  setConfirmRemove(false);
                },
              },
            );
          }}
          title={`Remove the keyword ${saved.keyword}?`}
          message={`Texting ${saved.keyword} to ${connection.from ?? "your number"} will get no answer. People who already joined stay joined.`}
          confirmLabel="Remove keyword"
          danger
          loading={save.isPending}
        />
      )}
    </Stack>
  );
}
